import React, { useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle, Clock } from "lucide-react";
import { DateTimeUtil } from "../../../utils";
import { ApprovalDetail } from "../../../types/state/timecards";

interface ApprovalDetailsSectionProps {
    approvalDetails: ApprovalDetail[];
}

const ApprovalDetailsSection: React.FC<ApprovalDetailsSectionProps> = ({ approvalDetails }) => {
    const [showApprovalDetails, setShowApprovalDetails] = useState<boolean>(false);

    if (!approvalDetails || approvalDetails.length === 0) {
        return null;
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
            <button
                onClick={() => setShowApprovalDetails(!showApprovalDetails)}
                className="w-full px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
            >
                <h2 className="text-lg font-semibold text-gray-900">Approval Details</h2>
                {showApprovalDetails ? (
                    <ChevronUp className="text-gray-600" size={20} />
                ) : (
                    <ChevronDown className="text-gray-600" size={20} />
                )}
            </button>

            {showApprovalDetails && (
                <div className="p-6">
                    <div className="space-y-6">
                        {approvalDetails.map((approval, index) => {
                            // Handle both string and number comparison
                            const statusCode = approval.current_status_code_txn;
                            const isApproved =
                                statusCode !== undefined &&
                                statusCode !== null &&
                                (statusCode === 1 || Number(statusCode) === 1);
                            // const approverNames =
                            //     approval.wf_task_actor?.EMPLOYEE?.map((emp) => emp.employee_name).join(", ") ||
                            //     "N/A";

                            // const approverNames =
                            //     approval.wf_task_acted?.EMP_NAME ||
                            //     "N/A";

                            let approverNames = "";
                            switch (statusCode) {
                                case 0:
                                    approverNames =
                                        approval.wf_task_actor?.EMPLOYEE?.map((emp) => emp.employee_name).join(", ") ||
                                        "N/A";
                                    break;
                                case 1:
                                    approverNames = approval.wf_task_acted?.EMP_NAME || "N/A";
                                    break;
                                default:
                                    approverNames = "N/A";
                                    break;
                            }

                            return (
                                <div key={index} className="relative">
                                    {/* Vertical line connecting steps */}
                                    {index < approvalDetails.length - 1 && (
                                        <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-300"></div>
                                    )}

                                    <div className="flex items-start space-x-4">
                                        {/* Status Icon */}
                                        <div className="flex-shrink-0 mt-1">
                                            {isApproved ? (
                                                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                                    <CheckCircle className="text-green-600" size={20} />
                                                </div>
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                                                    <Clock className="text-yellow-600" size={20} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Approval Details */}
                                        <div className="flex-1">
                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-base text-blue-600">
                                                        Level {approval.step_code} -{" "}
                                                        {approval.wf_task_actor?.TYPE === "ASSIGNED_MANAGER"
                                                            ? "Manager Approval"
                                                            : approval.wf_task_actor?.TYPE === "EMPLOYEE_NAME"
                                                            ? "Payroll Approval"
                                                            : "Approval"}
                                                    </h3>
                                                </div>

                                                <div className="border-t border-gray-300 pt-3 mt-3">
                                                    {isApproved ? (
                                                        <div className="flex items-start space-x-2">
                                                            <CheckCircle
                                                                className="text-green-600 flex-shrink-0 mt-0.5"
                                                                size={16}
                                                            />
                                                            <div>
                                                                <div className="text-gray-900">Approved</div>
                                                                <div className="text-sm text-gray-700 mt-1">
                                                                    {approverNames}
                                                                </div>
                                                                {approval.approved_on && (
                                                                    <div className="text-sm text-gray-600 mt-1">
                                                                        {DateTimeUtil.formatShortDate(
                                                                            approval.approved_on
                                                                        )}
                                                                        {/* {" "}
                                                                        •{" "}
                                                                        {DateTimeUtil.getFormattedTime(
                                                                            approval.approved_on
                                                                        )} */}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-start space-x-2">
                                                            <Clock
                                                                className="text-yellow-600 flex-shrink-0 mt-0.5"
                                                                size={16}
                                                            />
                                                            <div>
                                                                <div className="text-gray-900">Pending</div>
                                                                <div className="text-sm text-blue-600 mt-1">
                                                                    Waiting for {approverNames}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalDetailsSection;
